import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  onSnapshot,
  orderBy,
  Timestamp,
  deleteField,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '@/lib/firebase';
import type { Connector, PartnerDocument } from '@/types';

// Collection reference
const channelPartnersCollection = collection(db, 'channelPartners');

/**
 * Change history entry for tracking edits to a channel partner
 */
export interface ChangeHistoryEntry {
  id?: string;
  changedAt: Date;
  changedBy: string;
  changes: { field: string; oldValue: unknown; newValue: unknown }[];
}

/**
 * Add a change history entry to a partner's changeHistory subcollection
 * @param partnerId - The partner ID
 * @param changedBy - The user ID who made the change
 * @param changes - Array of field changes with old and new values
 */
export const addChangeHistoryEntry = async (
  partnerId: string,
  changedBy: string,
  changes: { field: string; oldValue: unknown; newValue: unknown }[]
): Promise<void> => {
  if (!changes.length) return;
  try {
    const historyCollection = collection(db, 'channelPartners', partnerId, 'changeHistory');
    await addDoc(historyCollection, {
      changedAt: Timestamp.now(),
      changedBy,
      changes: changes.map(c => ({
        field: c.field,
        oldValue: c.oldValue ?? null,
        newValue: c.newValue ?? null,
      })),
    });
  } catch (error) {
    console.error('Error adding change history:', error);
    // Non-critical - don't throw, just log
  }
};

/**
 * Upload a document file to Firebase Storage
 * @param file - The file to upload
 * @param userId - The user ID for file organization
 * @param partnerId - Optional partner ID for organizing files
 * @returns The document object with download URL
 */
export const uploadPartnerDocument = async (
  file: File,
  userId: string,
  partnerId?: string
): Promise<PartnerDocument> => {
  try {
    // Generate unique filename with timestamp to prevent overwrites
    const timestamp = Date.now();
    const uniqueFilename = `${timestamp}-${file.name}`;
    
    // Create storage path: channelPartners/{userId}/{partnerId or 'temp'}/{filename}
    const storagePath = `channelPartners/${userId}/${partnerId || 'temp'}/${uniqueFilename}`;
    const storageRef = ref(storage, storagePath);
    
    // Upload file
    const uploadResult = await uploadBytes(storageRef, file);
    
    // Get download URL
    const downloadURL = await getDownloadURL(uploadResult.ref);
    
    // Return document object
    const document: PartnerDocument = {
      id: `doc_${timestamp}_${Math.random().toString(36).substring(7)}`,
      name: file.name,
      type: file.type,
      size: file.size,
      url: downloadURL,
    };
    
    return document;
  } catch (error) {
    console.error('Error uploading document:', error);
    throw new Error(`Failed to upload document: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Delete a document file from Firebase Storage
 * @param fileUrl - The download URL of the file to delete
 */
export const deletePartnerDocument = async (fileUrl: string): Promise<void> => {
  try {
    // Skip if it's a base64 data URL or missing
    if (!fileUrl || fileUrl.startsWith('data:')) {
      return;
    }
    
    // Extract storage path from download URL
    const url = new URL(fileUrl);
    const pathMatch = url.pathname.match(/\/o\/(.+)$/);
    
    if (!pathMatch) {
      console.warn('Could not extract storage path from URL:', fileUrl);
      return;
    }
    
    const storagePath = decodeURIComponent(pathMatch[1]);
    const fileRef = ref(storage, storagePath);
    
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting file from storage:', error);
    // Don't throw - allow the partner to be deleted even if file deletion fails
  }
};

/**
 * Delete all documents for a channel partner
 * @param documents - Array of partner documents to delete
 */
export const deletePartnerDocuments = async (documents: PartnerDocument[]): Promise<void> => {
  if (!documents || documents.length === 0) {
    return;
  }
  
  const deletePromises = documents.map(document => {
    if (document.url) {
      return deletePartnerDocument(document.url);
    }
    return Promise.resolve();
  });
  
  await Promise.all(deletePromises);
};

/**
 * Delete visiting card photo from Firebase Storage
 * @param photoUrl - The download URL of the photo to delete
 */
export const deleteVisitingCardPhoto = async (photoUrl?: string): Promise<void> => {
  if (photoUrl) {
    await deletePartnerDocument(photoUrl);
  }
};

// Convert Firestore Timestamp to Date
const timestampToDate = (timestamp: unknown): Date => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  return new Date();
};

// Convert Channel Partner from Firestore format to app format
const convertFirestorePartner = (docData: Record<string, unknown>): Connector => {
  return {
    ...docData,
    createdAt: timestampToDate(docData.createdAt),
    updatedAt: docData.updatedAt ? timestampToDate(docData.updatedAt) : undefined,
    lastVisitDate: docData.lastVisitDate ? timestampToDate(docData.lastVisitDate) : undefined,
    lastFollowUpDate: docData.lastFollowUpDate ? timestampToDate(docData.lastFollowUpDate) : undefined,
    deactivatedAt: docData.deactivatedAt ? timestampToDate(docData.deactivatedAt) : undefined,
  } as Connector;
};

/**
 * Subscribe to channel partners for a specific user
 * @param userId - The user ID to filter channel partners
 * @param callback - Callback function to handle channel partners updates
 * @returns Unsubscribe function
 */
export const subscribeToChannelPartners = (
  userId: string,
  callback: (partners: Connector[]) => void
): (() => void) => {
  // Get partners created by user
  const createdByQuery = query(
    channelPartnersCollection,
    where('createdBy', '==', userId),
    orderBy('createdAt', 'desc')
  );

  // Get partners shared with user
  const sharedWithQuery = query(
    channelPartnersCollection,
    where('sharedWith', 'array-contains', userId),
    orderBy('createdAt', 'desc')
  );

  let createdPartners: Connector[] = [];
  let sharedPartners: Connector[] = [];

  const updatePartners = () => {
    // Merge both arrays and remove duplicates by ID
    const partnersMap = new Map<string, Connector>();
    
    [...createdPartners, ...sharedPartners].forEach(partner => {
      partnersMap.set(partner.id, partner);
    });
    
    const uniquePartners = Array.from(partnersMap.values());
    uniquePartners.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
    callback(uniquePartners);
  };

  // Subscribe to both queries
  const unsubscribeCreated = onSnapshot(
    createdByQuery,
    (snapshot) => {
      createdPartners = snapshot.docs.map((doc) => {
        const data = doc.data();
        return convertFirestorePartner({ ...data, id: doc.id });
      });
      updatePartners();
    },
    (error) => {
      console.error('Error fetching created channel partners:', error);
      createdPartners = [];
      updatePartners();
    }
  );

  const unsubscribeShared = onSnapshot(
    sharedWithQuery,
    (snapshot) => {
      sharedPartners = snapshot.docs.map((doc) => {
        const data = doc.data();
        return convertFirestorePartner({ ...data, id: doc.id });
      });
      updatePartners();
    },
    (error) => {
      console.error('Error fetching shared channel partners:', error);
      sharedPartners = [];
      updatePartners();
    }
  );

  // Return combined unsubscribe function
  return () => {
    unsubscribeCreated();
    unsubscribeShared();
  };
};

/**
 * Add a new channel partner
 * @param partner - The channel partner data (without id)
 * @param userId - The user ID who is creating the partner
 * @returns The created channel partner with ID
 */
export const addChannelPartner = async (
  partner: Omit<Connector, 'id'>,
  userId: string
): Promise<Connector> => {
  try {
    // Duplicate check: same user cannot add same phone number twice.
    // Uses createdBy filter to satisfy Firestore security rules.
    if (partner.whatsappNumber) {
      const dupQ = query(
        channelPartnersCollection,
        where('createdBy', '==', userId),
        where('whatsappNumber', '==', partner.whatsappNumber)
      );
      const dupSnap = await getDocs(dupQ);
      if (!dupSnap.empty) {
        throw new Error('DUPLICATE_CP: A channel partner with this WhatsApp number already exists in your list.');
      }
    }

    const now = Timestamp.now();
    const partnerData: Record<string, unknown> = {
      ...partner,
      createdBy: userId,
      createdAt: now,
      updatedAt: now,
    };

    // Remove undefined values (Firestore doesn't accept undefined)
    Object.keys(partnerData).forEach(key => {
      if (partnerData[key] === undefined) {
        delete partnerData[key];
      }
    });

    const docRef = await addDoc(channelPartnersCollection, partnerData);
    
    return {
      ...partner,
      id: docRef.id,
      createdBy: userId,
      createdAt: now.toDate(),
      updatedAt: now.toDate(),
    };
  } catch (error) {
    console.error('Error adding channel partner:', error);
    throw new Error(`Failed to add channel partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Update an existing channel partner
 * @param partnerId - The ID of the partner to update
 * @param updates - Partial channel partner data to update
 * @returns Promise that resolves when update is complete
 */
export const updateChannelPartner = async (
  partnerId: string,
  updates: Partial<Connector>
): Promise<void> => {
  try {
    const partnerRef = doc(db, 'channelPartners', partnerId);
    const updateData: Record<string, unknown> = {
      ...updates,
      updatedAt: Timestamp.now(),
    };
    
    // Handle undefined values - remove them but preserve explicit deletions
    Object.keys(updateData).forEach(key => {
      if (updateData[key] === undefined) {
        delete updateData[key];
      }
    });
    
    // If fields were explicitly set to undefined in updates, use deleteField to remove from Firestore
    if ('sharedWith' in updates && updates.sharedWith === undefined) {
      updateData.sharedWith = deleteField();
    }
    if ('deactivatedAt' in updates && updates.deactivatedAt === undefined) {
      updateData.deactivatedAt = deleteField();
    }
    if ('deactivationReason' in updates && updates.deactivationReason === undefined) {
      updateData.deactivationReason = deleteField();
    }
    
    await updateDoc(partnerRef, updateData);
  } catch (error) {
    console.error('Error updating channel partner:', error);
    throw new Error(`Failed to update channel partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Delete a channel partner and all associated files
 * @param partnerId - The ID of the partner to delete
 * @param partner - The full partner object (for deleting files)
 * @returns Promise that resolves when deletion is complete
 */
export const deleteChannelPartner = async (
  partnerId: string,
  partner: Connector
): Promise<void> => {
  try {
    // Delete all documents
    if (partner.documents && partner.documents.length > 0) {
      await deletePartnerDocuments(partner.documents);
    }
    
    // Delete visiting card photo
    if (partner.visitingCardPhoto) {
      await deleteVisitingCardPhoto(partner.visitingCardPhoto);
    }
    
    // Delete the partner document
    const partnerRef = doc(db, 'channelPartners', partnerId);
    await deleteDoc(partnerRef);
  } catch (error) {
    console.error('Error deleting channel partner:', error);
    throw new Error(`Failed to delete channel partner: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
};

/**
 * Toggle favorite status of a channel partner
 * @param partnerId - The ID of the partner to update
 * @param isFavorite - The new favorite status
 * @returns Promise that resolves when update is complete
 */
export const togglePartnerFavorite = async (
  partnerId: string,
  isFavorite: boolean
): Promise<void> => {
  await updateChannelPartner(partnerId, { isFavorite });
};

/**
 * Fetch every channel partner in the collection, regardless of owner.
 *
 * Admin / business head / operations only - firestore.rules gates this via
 * canReadOperationalStats(). A one-shot read rather than onSnapshot so the
 * Network Map does not hold a live listener over the whole collection.
 *
 * @returns All channel partners, newest first
 */
export const getAllChannelPartners = async (): Promise<Connector[]> => {
  // Let permission-denied propagate so callers can distinguish "no access"
  // from "no data" instead of silently rendering an empty map.
  const snapshot = await getDocs(
    query(channelPartnersCollection, orderBy('createdAt', 'desc'))
  );

  return snapshot.docs.map((docSnap) =>
    convertFirestorePartner({ ...docSnap.data(), id: docSnap.id })
  );
};

/**
 * Read a single channel partner directly by ID.
 *
 * Used when a partner is not present in the caller's own/shared list - e.g. an
 * admin opening a partner from the Network Map that another user created.
 *
 * @param partnerId - The partner ID
 * @returns The partner, or null if it does not exist
 */
export const getChannelPartnerById = async (
  partnerId: string
): Promise<Connector | null> => {
  const snapshot = await getDoc(doc(db, 'channelPartners', partnerId));

  if (!snapshot.exists()) {
    return null;
  }

  return convertFirestorePartner({ ...snapshot.data(), id: snapshot.id });
};
